import type { SentenceTiming, WordTiming } from '@slideforge/shared';

export interface CharacterAlignment {
  readonly characters: readonly string[];
  readonly characterStartTimesSeconds: readonly number[];
  readonly characterEndTimesSeconds: readonly number[];
}

export interface DerivedTimings {
  readonly words: readonly WordTiming[];
  readonly sentences: readonly SentenceTiming[];
}

interface AlignedCharacter {
  readonly start: number;
  readonly end: number;
  readonly startMs: number;
  readonly endMs: number;
}

/** Converts ElevenLabs character alignment into the provider-neutral M6 timing shape. */
export function timingsFromCharacterAlignment(
  text: string,
  alignment: CharacterAlignment,
): DerivedTimings {
  const { characters, characterStartTimesSeconds: starts, characterEndTimesSeconds: ends } = alignment;
  if (characters.length !== starts.length || characters.length !== ends.length) {
    throw new Error('The TTS provider returned alignment arrays with different lengths.');
  }

  const alignedText = characters.join('');
  if (alignedText !== text) {
    throw new Error('The TTS provider alignment does not match the transmitted narration text.');
  }

  let offset = 0;
  const aligned: AlignedCharacter[] = characters.map((character, index) => {
    const start = offset;
    offset += character.length;
    return {
      start,
      end: offset,
      startMs: secondsToMs(starts[index] as number),
      endMs: secondsToMs(ends[index] as number),
    };
  });

  const words = matches(text, /[\p{L}\p{N}]+(?:['’-][\p{L}\p{N}]+)*/gu).map((match) =>
    timingForRange(text, match.start, match.end, aligned),
  );
  const sentences = matches(text, /[^.!?\n]+(?:[.!?]+|$)/gu)
    .map((match) => trimRange(text, match.start, match.end))
    .filter((range) => range.end > range.start)
    .map((range) => sentenceTimingForRange(text, range.start, range.end, aligned));

  return { words, sentences };
}

/** Resolves authored character markers to absolute animation delays. */
export function syncMarkersFromCharacterOffsets(
  offsets: readonly number[],
  words: readonly WordTiming[],
): readonly number[] {
  return offsets.map((offset) => {
    const word =
      words.find((candidate) => offset >= candidate.charStart && offset < candidate.charEnd) ??
      words.find((candidate) => candidate.charStart >= offset) ??
      words[words.length - 1];
    return word?.startMs ?? 0;
  });
}

function timingForRange(
  text: string,
  start: number,
  end: number,
  aligned: readonly AlignedCharacter[],
): WordTiming {
  const timing = alignedRange(start, end, aligned);
  return { word: text.slice(start, end), startMs: timing.startMs, endMs: timing.endMs, charStart: start, charEnd: end };
}

function sentenceTimingForRange(
  text: string,
  start: number,
  end: number,
  aligned: readonly AlignedCharacter[],
): SentenceTiming {
  const timing = alignedRange(start, end, aligned);
  return { text: text.slice(start, end), startMs: timing.startMs, endMs: timing.endMs, charStart: start, charEnd: end };
}

function alignedRange(
  start: number,
  end: number,
  aligned: readonly AlignedCharacter[],
): { startMs: number; endMs: number } {
  const members = aligned.filter((item) => item.end > start && item.start < end);
  const first = members[0];
  const last = members[members.length - 1];
  if (!first || !last) throw new Error('The TTS provider omitted timing for part of the narration text.');
  return { startMs: first.startMs, endMs: Math.max(first.startMs, last.endMs) };
}

function matches(text: string, pattern: RegExp): { start: number; end: number }[] {
  return [...text.matchAll(pattern)].map((match) => ({
    start: match.index,
    end: match.index + match[0].length,
  }));
}

function trimRange(text: string, start: number, end: number): { start: number; end: number } {
  while (start < end && /\s/u.test(text[start] as string)) start += 1;
  while (end > start && /\s/u.test(text[end - 1] as string)) end -= 1;
  return { start, end };
}

function secondsToMs(value: number): number {
  if (!Number.isFinite(value) || value < 0) throw new Error('The TTS provider returned an invalid timestamp.');
  return Math.round(value * 1_000);
}
